import * as THREE from "three";
import { usePhysicsStore } from "../../stores/physicsStore";

export function getConnectBodyAnchorState(body1Obj, body2Obj, body1Anchor) {
  const body1AnchorVec = new THREE.Vector3(
    Number(body1Anchor?.[0]) || 0,
    Number(body1Anchor?.[1]) || 0,
    Number(body1Anchor?.[2]) || 0
  );

  body1Obj.updateMatrixWorld(true);
  body2Obj.updateMatrixWorld(true);

  const body1World = body1AnchorVec.clone().applyMatrix4(body1Obj.matrixWorld);
  const body2AnchorVec = body1World
    .clone()
    .applyMatrix4(new THREE.Matrix4().copy(body2Obj.matrixWorld).invert());
  const body2World = body2AnchorVec.clone().applyMatrix4(body2Obj.matrixWorld);

  return {
    body1Anchor: body1AnchorVec.toArray(),
    body2Anchor: body2AnchorVec.toArray(),
    body1World,
    body2World,
  };
}

export function getConnectBodyAnchorWorldPositions(userData) {
  const { body1, body2, body1Anchor, body2Anchor } = userData || {};
  if (!body1 || !body2 || !body1Anchor || !body2Anchor) return null;

  body1.updateMatrixWorld(true);
  body2.updateMatrixWorld(true);

  const body1World = new THREE.Vector3(...body1Anchor).applyMatrix4(body1.matrixWorld);
  if (!usePhysicsStore.getState().isRunning) {
    return {
      body1World,
      body2World: body1World.clone(),
    };
  }

  return {
    body1World,
    body2World: new THREE.Vector3(...body2Anchor).applyMatrix4(body2.matrixWorld),
  };
}

export function getConnectBodyDisplayLinePoints(userData, anchorPositions = null) {
  const positions = anchorPositions || getConnectBodyAnchorWorldPositions(userData);
  if (!positions) return null;
  const gap = positions.body1World.distanceTo(positions.body2World);
  return {
    points: [positions.body1World, positions.body2World],
    gap,
    isAuthoredSpan: false,
  };
}

// Joint visualization lives under the body container (`group`), not under `poseGroup`, so it does not
// inherit physics transforms applied to `poseGroup`. While physics runs, sample the pivot through the
// live pose group; once physics stops, return to the authored parent-frame joint viz position.
export function setJointPivotWorldFromViz(target, vizGroup, jointObjectsRef) {
  const jointUuid = vizGroup?.userData?.jointUuid;
  const entry = jointUuid ? jointObjectsRef?.current?.[jointUuid] : null;
  const poseGroup = entry?.bodyGroup;
  const jp = entry?.joint?.position;
  const isPhysicsRunning = usePhysicsStore.getState().isRunning;
  if (isPhysicsRunning && poseGroup && Array.isArray(jp) && jp.length >= 3) {
    poseGroup.updateMatrixWorld(true);
    target.set(Number(jp[0]) || 0, Number(jp[1]) || 0, Number(jp[2]) || 0);
    target.applyMatrix4(poseGroup.matrixWorld);
    return;
  }
  if (vizGroup) vizGroup.getWorldPosition(target);
}

export const ARC_CHORD_EPS = 1e-10;

export function canonicalJointPairKey(jointUuidA, jointUuidB) {
  const s1 = String(jointUuidA ?? "");
  const s2 = String(jointUuidB ?? "");
  return s1 <= s2 ? `${s1}\0${s2}` : `${s2}\0${s1}`;
}

/** Pairs where a 2-member fixed tendon shares both joints with a joint equality — arcs split via bow scale + spheres. */
export function jointPairsWithCoincidentTendonAndJointEq(tendonsMap, equalitiesMap) {
  const tendonPairs = new Set();
  Object.values(tendonsMap || {}).forEach((tendon) => {
    const refs = (tendon?.joints || []).map((j) => j?.joint).filter(Boolean);
    if (refs.length !== 2) return;
    const [r0, r1] = refs;
    if (r0 !== r1) tendonPairs.add(canonicalJointPairKey(r0, r1));
  });
  const out = new Set();
  Object.values(equalitiesMap || {}).forEach((eq) => {
    if (eq?.type !== "joint" || !eq.obj1 || !eq.obj2) return;
    if (eq.obj1 === eq.obj2) return;
    const key = canonicalJointPairKey(eq.obj1, eq.obj2);
    if (tendonPairs.has(key)) out.add(key);
  });
  return out;
}

/** Same-direction arc bow when tendon + joint equality share two joints (equality subtler, tendon taller). */
export const COINCIDENT_JOINT_EQ_ARC_BOW_SCALE = 0.72;
export const COINCIDENT_TENDON_ARC_BOW_SCALE = 1.38;

/**
 * Curve control points for a tendon / joint-equality arc. Two joints: bulge midpoint along an axis
 * perpendicular to chord (fallback world +Y when chord is vertical). bowScale amplifies magnitude (>0).
 */
export function buildTendonCurvePoints(positions, bowScale = 1) {
  if (!positions || positions.length !== 2) return positions;
  const p0 = positions[0];
  const p1 = positions[1];
  const mid = new THREE.Vector3().lerpVectors(p0, p1, 0.5);
  const chord = new THREE.Vector3().subVectors(p1, p0);
  const dist = chord.length();
  if (dist < ARC_CHORD_EPS) return [p0, mid, p1];
  chord.multiplyScalar(1 / dist);
  const worldUp = new THREE.Vector3(0, 1, 0);
  const axis = new THREE.Vector3().crossVectors(chord, worldUp);
  if (axis.lengthSq() < ARC_CHORD_EPS * ARC_CHORD_EPS) {
    axis.copy(worldUp);
  } else {
    axis.normalize();
  }
  const magnitude = Math.max(dist * 0.15, 0.02);
  mid.addScaledVector(axis, bowScale * magnitude);
  return [p0, mid, p1];
}
